import { refreshAuthServiceSession } from "../app/modules/Auth/_redux/authCrud";
import * as auth from "../app/modules/Auth/_redux/authRedux";
import { ROUTES_PATH } from "../app/constants/Routes";

/** Renova o access token antes de expirar (margem em ms). */
const ACCESS_TOKEN_REFRESH_BUFFER_MS = 60_000;

/** Renovação descartada porque a sessão mudou embaixo dela. */
export const SESSION_CHANGED = "SESSION_CHANGED";

const OTP_VERIFY_API_SUFFIXES = [
  "/auth/2fa/verify",
  "/auth/first-access/verify-code",
];

const OTP_BEARER_API_SUFFIXES = [
  ...OTP_VERIFY_API_SUFFIXES,
  "/auth/2fa/resend-code",
  "/auth/first-access/resend-code",
  "/auth/first-access/send-code",
  "/auth/first-access/registration-ready",
  "/auth/first-access/setup-password",
  "/auth/first-access/deny",
];

let pendingRefresh = null;

function isOtpBearerApiRequest(url) {
  return OTP_BEARER_API_SUFFIXES.some(suffix => url?.endsWith(suffix));
}

function isAuthServiceRefreshRequest(url) {
  return String(url || "").includes("/oauth/token/refresh");
}

const CREDENTIAL_REJECTION_STATUSES = [400, 401, 403];

function isCredentialRejectionError(error) {
  return CREDENTIAL_REJECTION_STATUSES.includes(error?.response?.status);
}

function normalizeExpiresIn(value) {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

async function performAuthServiceRefresh(store, idToken) {
  const data = await refreshAuthServiceSession();
  if (!data?.access_token) {
    throw new Error("refresh missing access_token");
  }
  const state = store.getState().auth;
  if (state.idToken !== idToken) {
    throw new Error(SESSION_CHANGED);
  }
  const nextIdToken =
    typeof data.id_token === "string" && data.id_token ? data.id_token : state.idToken;
  const expiresIn = normalizeExpiresIn(data.expires_in);
  store.dispatch(auth.actions.refreshTokens(data.access_token, expiresIn, nextIdToken));
  return data;
}

// Shares one in-flight refresh (network call + session validation + token dispatch) across
// concurrent callers for the same idToken, so they all observe the same applied result instead
// of each re-validating against a store that a sibling call may have already updated.
function completeAuthServiceRefresh(store, idToken) {
  if (pendingRefresh?.key === idToken) {
    return pendingRefresh.promise;
  }

  const promise = performAuthServiceRefresh(store, idToken).finally(() => {
    if (pendingRefresh?.key === idToken) {
      pendingRefresh = null;
    }
  });
  pendingRefresh = { key: idToken, promise };

  return promise;
}

function endAuthServiceSession() {
  if (typeof window === "undefined" || window.location.pathname === ROUTES_PATH.AUTH.LOGOUT) {
    return;
  }
  window.location.href = ROUTES_PATH.AUTH.LOGOUT;
}

async function runTokenRefreshIfExpired(store, config) {
  if (
    config.skipAuthRefresh ||
    isAuthServiceRefreshRequest(config.url) ||
    isOtpBearerApiRequest(config.url)
  ) {
    return;
  }

  const { authToken, idToken, accessTokenExpiresAt } = store.getState().auth;
  if (!auth.isAuthServiceSession({ idToken }) || !authToken || !accessTokenExpiresAt) {
    return;
  }
  if (Date.now() < accessTokenExpiresAt - ACCESS_TOKEN_REFRESH_BUFFER_MS) {
    return;
  }

  await completeAuthServiceRefresh(store, idToken);
}

export default function setupAxios(axios, store) {
  const ONE_MINUTE = 60000;

  axios.defaults.timeout = ONE_MINUTE;

  axios.interceptors.request.use(
    async config => {
      try {
        await runTokenRefreshIfExpired(store, config);
      } catch (error) {
        if (isCredentialRejectionError(error)) {
          endAuthServiceSession();
          return Promise.reject(new Error("SESSION_REFRESH_FAILED"));
        }
        const { accessTokenExpiresAt } = store.getState().auth;
        if (!accessTokenExpiresAt || Date.now() >= accessTokenExpiresAt) {
          return Promise.reject(new Error("SESSION_REFRESH_FAILED"));
        }
        // Transient failure (network error, 5xx, timeout) or a sibling request already
        // applied a refresh (SESSION_CHANGED) — the current access token is still valid,
        // so let this request through with it instead of forcing a logout.
      }

      const {
        auth: { authToken },
      } = store.getState();

      if (
        authToken &&
        !config.skipAuthRefresh &&
        !config.url.includes("api.pagar.me/core/v5/tokens") &&
        !isOtpBearerApiRequest(config.url)
      ) {
        config.headers.Authorization = `Bearer ${authToken}`;
      }

      return config;
    },
    err => Promise.reject(err)
  );

  axios.interceptors.response.use(
    response => response,
    async error => {
      const original = error?.config;

      if (
        error?.response?.status !== 401 ||
        original?.url?.endsWith("/auth/login") ||
        isOtpBearerApiRequest(original?.url) ||
        window.location.pathname === ROUTES_PATH.AUTH.LOGOUT
      ) {
        return Promise.reject(error);
      }

      const authState = store.getState().auth;
      if (auth.isAuthServiceSession(authState) && original && !original.skipAuthRefresh) {
        if (original._retryAuthRefresh || isAuthServiceRefreshRequest(original.url)) {
          endAuthServiceSession();
          return Promise.reject(error);
        }

        original._retryAuthRefresh = true;

        try {
          const data = await completeAuthServiceRefresh(store, authState.idToken);
          original.headers = original.headers || {};
          original.headers.Authorization = `Bearer ${data.access_token}`;
          return axios(original);
        } catch (refreshError) {
          if (isCredentialRejectionError(refreshError)) {
            endAuthServiceSession();
          }
          return Promise.reject(error);
        }
      }

      window.location.href = ROUTES_PATH.AUTH.LOGOUT;
      return Promise.reject(error);
    }
  );
}
